import { createApp } from "vue";
import App from "@/src/App_v2.vue";
import router from "@/src/router";
import store from "@/src/store";
import mitt from "mitt";
import Antd from "ant-design-vue";
import "@/src/styles/_app.css";
import { VueDraggableNext } from "vue-draggable-next";
import VueTelInput from "vue-tel-input";
import VueApexCharts from "vue3-apexcharts";
import "vue-tel-input/vue-tel-input.css";
import selectFixPlugin from "@/src/plugins/selectFixPlugin";
import beamerPlugin from "@/src/plugins/beamer.js";
import intercomPlugin from "@/src/plugins/intercom.js";
import EmojiPicker from "vue3-emoji-picker";
import "vue3-emoji-picker/css";
import * as Sentry from "@sentry/vue";
import { init as initErrorLogger } from "@superagi/otel-error-logger-js";
import { useCSVBoxState } from "@/src/utils/csvBoxState.js";
import MixpanelService from "@/src/services/mixpanel/mixpanel.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import themeAwareImage from "@/src/directives/themeAwareImage.js";

const emitter = mitt();
const app = createApp(App);

const csvBoxState = useCSVBoxState();
app.provide("csvBoxState", csvBoxState);

// mixpanel initialization
if (process.env.NODE_ENV === "production") {
  try {
    MixpanelService.init("60ad9f164c03691f0752517d4b29643c");
    app.config.globalProperties.$mixpanel = MixpanelService;
    app.provide("mixpanel", MixpanelService);
  } catch (error) {
    console.error("An error occurred while initializing mixpanel: ", error);
  }
} else console.log("Skipping Mixpanel Initialization");

// sentry initialization
if (process.env.NODE_ENV === "production") {
  try {
    console.log(
      "Initializing Sentry for production environment:: ",
      process.env.NODE_ENV,
    );

    Sentry.init({
      app,
      dsn: "https://8168e4679ef6f0eccebc77a16dc4ff8a@o4507983655010304.ingest.us.sentry.io/4507983658221568",
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: 1.0,
      tracePropagationTargets: ["https://sales.superagi.com"],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      environment: process.env.NODE_ENV,
    });
  } catch (error) {
    console.error("Error initializing Sentry:", error);
  }
} else console.log("Skipping Sentry Integration");

// OTel error logger initialization
if (process.env.NODE_ENV === "production") {
  try {
    initErrorLogger({
      serviceName: "super-sales-frontend",
      environment: process.env.NODE_ENV,
      endpoint: "https://error-logging-service.superagi.com/v1/logs",
      apiKey:
        "e743cd0ec73e557407795fabcbaccfdd249e5cc72bade81c1c0b7f5f892e4fd3",
      release: "1.0.0",
    });
  } catch (error) {
    console.error("Error initializing OTel error logger:", error);
  }
}

app.use(router);
app.use(store);
app.use(Antd);
app.use(VueTelInput);
app.use(VueApexCharts);
app.use(selectFixPlugin);
app.use(beamerPlugin, { productId: "QQqPTFmS70064" });
app.use(intercomPlugin);
app.config.globalProperties.emitter = emitter;
app.component("draggable-next", VueDraggableNext);
app.component("EmojiPicker", EmojiPicker);
app.component("Apexchart", VueApexCharts);
app.component("ThemeImage", ThemeImage);

app.directive("theme-image", themeAwareImage);

(async () => {
  try {
    app.mount("#app_v2");

    await router.isReady();
    const isPublicRoute = router.currentRoute.value.meta.public === true;
    if (!isPublicRoute) {
      Promise.all([
        store.dispatch("getStarted/fetchStatus"),
        store.dispatch("rbac/fetchPermissions", { force: true }),
        store.dispatch("billing/getBillingDetails"),
      ]).catch(error => {
        if (error.response?.status !== 401) {
          console.error("Error loading initial data:", error);
        }
      });
    }
  } catch (error) {
    console.error("Error initializing app:", error);
    app.mount("#app_v2");
  }
})();
